import styled from 'styled-components';
import React from 'react';
import { ControlBar, Badge, Icon, palette } from '@m12s/component-library';
import {
  LoadingTextPlaceholder,
  loadingFade,
} from 'components/LoadingTextPlaceholder';

// Operators were confused by icon-only control-bar buttons, so we always show a
// text label (the demo never collapses to icons). Keeping this at 0 means the
// short/icon-only tier never applies — every button shows at least its medium
// label. `fullTextBreakpoint` still swaps medium → full on wide screens.
const iconsOnlyBreakpoint = 0;
// Below this viewport width the control-bar buttons show their shorter (medium)
// label so a crowded landscape bar collapses (e.g. "Enter Setup" → "Setup")
// instead of horizontal-scrolling. (Container queries would be ideal here but
// styled-components' stylis strips @container declarations, so viewport it is.)
const fullTextBreakpoint = 90;

const ButtonBadge = styled(Badge)`
  @media (min-width: ${iconsOnlyBreakpoint}rem) {
    margin-right: 0.75rem;
  }
`;

const ControlBarResponsive = styled(ControlBar)`
  position: relative;

  /* Match the demo's control bar: semibold (600) labels at 20px rather than
     bold (700)/24px. Scoped to the button text (spans) so the FontAwesome svg
     icons and the shared TextMedia used by the stat tabs are unaffected. */
  button {
    font-weight: 600;
  }

  button span {
    font-size: 1.25rem;
  }

  @media (orientation: portrait) {
    button {
      flex: unset;
      flex-grow: 1;
    }
  }
`;

const Scrollable = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
`;

const SingleButtonScrollable = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
`;

const ControlBarContent = styled.div`
  @media (max-width: 30rem) {
    span {
    }
  }
`;

// Always paired with a text label now, so the icon sits at the demo's label
// size (1.25rem / 20px) with a right margin rather than the old large icon-only
// treatment.
const MobileIcon = styled(Icon)`
  color: ${({ disabled }) => {
    return disabled ? palette.Grey400 : palette.Grey500;
  }};
  font-size: 1.25rem;
  margin-right: 0.75rem;
`;

// Control-bar label. Below fullTextBreakpoint the button shows its medium
// (shorter) label so a crowded bar collapses instead of horizontal-scrolling;
// at/above it, the full label. Never icon-only (short is kept blank on purpose
// but iconsOnlyBreakpoint=0 means that tier never applies).
const ResponsiveText = ({ full, medium, short }) => {
  return (
    <>
      <TextMedia max={iconsOnlyBreakpoint}>{short}</TextMedia>
      <TextMedia min={iconsOnlyBreakpoint} max={fullTextBreakpoint}>
        {medium}
      </TextMedia>
      <TextMedia min={fullTextBreakpoint}>{full}</TextMedia>
    </>
  );
};

const TextMedia = styled.span`
  display: none;
  @media (min-width: ${({ min }) => {
      return min || 0;
    }}rem) and (max-width: ${({ max }) => {
      return max - 0.01 || 1000;
    }}rem) {
    display: inline;
  }
`;

const LoadingStatTabLabel = styled.div`
  ${({ loading }) => {
    return loading && loadingFade;
  }}
`;

const LoadingStatTabSecondaryLabel = styled(LoadingTextPlaceholder)`
  height: 1rem;
  width: 5rem;
  border-radius: 2px;

  @media (orientation: portrait) {
    width: 3rem;
  }
`;

export {
  ButtonBadge,
  ControlBarResponsive,
  Scrollable,
  SingleButtonScrollable,
  ControlBarContent,
  MobileIcon,
  ResponsiveText,
  TextMedia,
  LoadingStatTabLabel,
  LoadingStatTabSecondaryLabel,
};
